import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef } from 'react';
import axiosInstance from './api/axiosInstance';
import {
  useAuth,
  LoginRequiredModal,
  LoginRequiredModalProvider,
  NewsletterPrompt,
  LoadingProvider,
  SCREENS,
  useMediaQuery,
  FooterV2,
} from '@repo/common';

import ErrorCS from './pages/Error/ErrorCS';
import SessionExpired from './pages/Session/SessionExpired';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import VerifyEmail from './pages/Auth/VerifyEmail';
import GoogleCallback from './pages/Auth/GoogleCallback';
import AppleCallback from './pages/Auth/AppleCallback';
import AdditionalInfo from './pages/Auth/AdditionalInfo';

import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ForgotPasswordVerify from './pages/ForgotPassword/ForgotPasswordVerify';
import ForgotPasswordNew from './pages/ForgotPassword/ForgotPasswordNew';
import ForgotPasswordNewSuccess from './pages/ForgotPassword/ForgotPasswordNewSuccess';

import Qna from './pages/Qna/Qna';
import QnaDetail from './pages/Qna/QnaDetail';
import QnaSearch from './pages/Qna/QnaSearch';
import Faq from './pages/Faq/Faq.js';
import Article from './pages/Article/Article';
import ArticleDetail from './pages/Article/ArticleDetail';

import {
  TermsPage,
  PrivacyPage,
  RefundPolicyPage,
  BusinessTermsPage,
  BusinessPrivacyPage,
} from './pages/Policy/PolicyDocument.js';

import Career from './pages/Career/Career';
import CreateResume from './pages/Career/CreateResume';
import CreateResumeSuccess from './pages/Career/CreateResumeSuccess';
import CareerResumeGuide from './pages/Career/CareerResumeGuide';

import GNB from './pages/Gnb/Gnb.js';
import LogoOnlyGNB from './pages/Gnb/LogoOnlyGnb.js';
import MinimalGnb from './pages/Gnb/MinimalGnb.js';
import Home from './pages/Home/Home.js';
import LegacyFooter from './pages/Footer/Footer.js';

import PathFeed from './pages/Kpathport/PathFeed';
import Discover from './pages/Kpathport/Discover';
import Workshop from './pages/Kpathport/Workshop';
import WorkshopCreate from './pages/Kpathport/WorkshopCreate';
import Booking from './pages/Kpathport/Booking';
import CheckDetails from './pages/Kpathport/CheckDetails';
import Confirmed from './pages/Kpathport/Confirmed';
import Payments from './pages/Payments/Payments.js';
import BankTransfer from './pages/Kpathport/BankTransfer';
import PaymentSuccess from './pages/Kpathport/PaymentSuccess';
import PaymentFail from './pages/Kpathport/PaymentFail';

import JobBoard from './pages/JobBoard/JobBoard';
import JobBoardDetail from './pages/JobBoard/JobBoardDetail';
import DetailUnavailable from './components/JobBoard/Detail/DetailUnavailable';
import MobileComingSoonPage from './components/Common/MobileComingSoonPage';
import PersonalOnlyBlocked from './components/Common/PersonalOnlyBlocked';
import { isBusinessRole } from './components/Gnb/jobNavigationConfig';
import JobBoardAdminDetail from './pages/JobBoard/JobBoardAdminDetail';
import JobPost from './pages/JobBoard/JobPost';

import Pathfinder from './pages/Pathfinder/Pathfinder.js';
import MyPage from './pages/MyPage/MyPage.js';
import DeleteAccount from './pages/MyPage/DeleteAccount/DeleteAccount.js';
import JobHidden from './pages/JobBoard/JobHidden.js';
import MyResumes from './pages/MyPage/MyResumes/MyResumes.js';
import RequestRefund from './pages/Refund/RequestRefund.js';
import RefundSuccess from './pages/Refund/RefundSuccess.js';

import PathfinderDashboard from './pages/PathfinderDashboard/PathfinderDashboard.js';
import WorkshopManagementDetail from './pages/MyPage/WorkshopManagementDetail/WorkshopManagementDetail.js';
import PathfinderEntry from './pages/Pathfinder/PathfinderEntry.js';

import KnameStart from './pages/Kname/KnameStart.js';
import KnameForm from './pages/Kname/KnameForm.js';
import KnameMood from './pages/Kname/KnameMood.js';
import KnameResult from './pages/Kname/KnameResult.js';

import IcebreakerGacha from './pages/IcebreakerGacha/IcebreakerGacha.js';

import MigrationModal from './components/Migration/MigrationModal.js';
import FeatureMoved from './components/Migration/FeaturedMoved.js';
import TwoDaysMoved from './pages/TwoDaysMoved';
import { IS_LEGACY_DOMAIN, toNewDomain } from './utils/brand.js';

const MOBILE_COMING_SOON_QUERY = `(max-width: ${SCREENS.tabletH - 0.02}px)`;

const isJobPostPath = (pathname) =>
  pathname === '/jobs/admin/new' || /^\/jobs\/admin\/[^/]+\/edit$/.test(pathname);

/** 공고 관리 상세(/jobs/admin/:jobId) — 등록 화면(/new)은 제외 */
const isAdminJobDetailPath = (pathname) =>
  /^\/jobs\/admin\/[^/]+$/.test(pathname) && pathname !== '/jobs/admin/new';

/** 공고 관리(목록·상세·등록·수정)는 모두 모바일 레이아웃이 있어 coming soon 대상이 아니다 */
const isMobileComingSoonPath = (pathname) => pathname.startsWith('/admin/workshop');

function MobileUnsupportedRoute({ children }) {
  const isMobile = useMediaQuery(MOBILE_COMING_SOON_QUERY);

  if (isMobile) {
    return <MobileComingSoonPage />;
  }

  return children;
}

function PaymentRoute({ children }) {
  const navigate = useNavigate();
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!isLoggedIn) {
    return <LoginRequiredModal isOpen onDismiss={() => navigate('/', { replace: true })} />;
  }

  return children;
}

const ADMIN_ROLES = ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'];
const JOB_MANAGER_ROLES = [...ADMIN_ROLES, 'E', 'ROLE_E', 'EMPLOYER', 'ROLE_EMPLOYER'];

function AdminOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  const isAdmin = ADMIN_ROLES.includes(String(user?.role ?? '').toUpperCase());

  return isAdmin ? children : <Navigate to='/' replace />;
}

function JobManagerRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  const canManageJobs = JOB_MANAGER_ROLES.includes(String(user?.role ?? '').toUpperCase());

  return canManageJobs ? children : <Navigate to='/' replace />;
}

/** 개인 계정 전용 기능. 비즈니스 계정이면 차단 상태 화면을 보여준다 */
function PersonalOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  return isBusinessRole(user?.role) ? <PersonalOnlyBlocked /> : children;
}

function LegacyRedirect() {
  useEffect(() => {
    window.location.replace(toNewDomain());
  }, []);
  return null;
}

function AppInner() {
  const navigate = useNavigate();

  const formRef = useRef();

  const handlePublish = () => {
    formRef.current?.handlePublish();
  };

  const handleSaveDraft = () => {
    formRef.current?.handleSaveDraft();
  };

  const RedirectToError = () => {
    const location = useLocation();
    useEffect(() => {
      sessionStorage.setItem('errorFrom', location.pathname + location.search);
    }, []);
    return <Navigate to='/error' replace />;
  };

  useEffect(() => {
    const rememberMe = localStorage.getItem('rememberMe');
    if (rememberMe !== 'true') return;

    const autoLogin = async () => {
      try {
        await axiosInstance.get('/users/auto-login');
      } catch (e) {
        localStorage.removeItem('rememberMe');
        if (
          !window.location.pathname.startsWith('/korean-name-generator') &&
          !window.location.pathname.startsWith('/icebreaker-gacha')
        ) {
          navigate('/login');
        }
      }
    };

    autoLogin();
  }, [navigate]);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith('/error')) {
      sessionStorage.removeItem('errorRetried');
    }
  }, [location.pathname]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search, location.hash]);
  const isMobileViewport = useMediaQuery(MOBILE_COMING_SOON_QUERY);
  const showMobileComingSoon = isMobileViewport && isMobileComingSoonPath(location.pathname);
  const isCheckDetailsPage = location.pathname.startsWith('/community/kpathport/check-details');
  const isBookingPage = location.pathname.startsWith('/community/kpathport/booking');
  const isConfirmedPage = location.pathname.startsWith('/community/kpathport/confirmed');
  const isJobPostPage = isJobPostPath(location.pathname);
  const isMyPageDeleteAccountPage = location.pathname.startsWith('/mypage/delete-account');
  const isPathfinderPage = !IS_LEGACY_DOMAIN && location.pathname === '/pathfinder';
  const isPathfinderOnboardingPage =
    !IS_LEGACY_DOMAIN && location.pathname.startsWith('/pathfinder/onboarding');
  const isCareerResumesHidden =
    location.pathname.startsWith('/career/resumes') &&
    !location.pathname.startsWith('/career/resumes/guide');
  const isMyPageMobileDetail =
    isMobileViewport &&
    location.pathname.startsWith('/mypage') &&
    !location.pathname.startsWith('/mypage/delete-account') &&
    new URLSearchParams(location.search).has('tab');
  const isMobileMyPageInvoice =
    isMobileViewport &&
    location.pathname.startsWith('/mypage') &&
    new URLSearchParams(location.search).has('invoice');
  const isHiddenJobsMobile = isMobileViewport && location.pathname.startsWith('/jobs/hidden/all');
  const isMyResumesMobile = isMobileViewport && location.pathname.startsWith('/mypage/resumes/all');
  const isRefundRequestPage = /^\/mypage\/[^/]+\/refund$/.test(location.pathname);
  const isRefundSuccessPage = /^\/mypage\/[^/]+\/refund\/success$/.test(location.pathname);

  const showLogoOnlyGnb =
    !showMobileComingSoon &&
    (location.pathname.startsWith('/community/kpathport/payments') ||
      location.pathname.startsWith('/community/kpathport/bank-transfer') ||
      location.pathname.endsWith('/success') ||
      location.pathname.startsWith('/community/kpathport/check-details') ||
      location.pathname.startsWith('/mypage/delete-account') ||
      isRefundRequestPage ||
      isRefundSuccessPage ||
      isPathfinderPage ||
      isPathfinderOnboardingPage);
  // 2days 워크보드는 자체 사이드바·헤더를 쓰므로 GNB·푸터를 숨긴다
  const isTwoDaysPage = location.pathname.startsWith('/2days');
  const hideGnb =
    showMobileComingSoon ||
    location.pathname.startsWith('/error') ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/verify') ||
    location.pathname.startsWith('/password') ||
    isCareerResumesHidden ||
    location.pathname.startsWith('/community/kpathport/check-details') ||
    location.pathname.startsWith('/jobs/admin') ||
    location.pathname.startsWith('/mypage/delete-account') ||
    isHiddenJobsMobile ||
    isMyResumesMobile ||
    isMyPageMobileDetail ||
    isRefundRequestPage ||
    isRefundSuccessPage ||
    isPathfinderPage ||
    isPathfinderOnboardingPage ||
    location.pathname.startsWith('/pathfinder/dashboard') ||
    location.pathname.startsWith('/korean-name-generator') ||
    location.pathname.startsWith('/icebreaker-gacha') ||
    isTwoDaysPage;
  const hideFooter =
    showMobileComingSoon ||
    location.pathname.startsWith('/error') ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/verify') ||
    location.pathname.startsWith('/password') ||
    isCareerResumesHidden ||
    location.pathname.startsWith('/community/qna/') ||
    location.pathname.startsWith('/community/kpathport/booking') ||
    location.pathname.startsWith('/community/kpathport/check-details') ||
    location.pathname.startsWith('/community/kpathport/payments') ||
    location.pathname.startsWith('/community/kpathport/bank-transfer') ||
    (location.pathname.startsWith('/jobs/admin') && location.pathname !== '/jobs/admin') ||
    location.pathname.startsWith('/mypage/delete-account') ||
    isPathfinderPage ||
    isPathfinderOnboardingPage ||
    location.pathname.startsWith('/pathfinder/dashboard') ||
    location.pathname.startsWith('/session-expired') ||
    isMobileMyPageInvoice ||
    location.pathname.startsWith('/korean-name-generator') ||
    location.pathname.startsWith('/icebreaker-gacha') ||
    isTwoDaysPage;
  // Article 상세는 본문 아래에 크리에이터 모집 배너를 붙인다 (디자인 시안 기준)
  const showCreatorSection =
    location.pathname.startsWith('/article/') && location.pathname !== '/article/admin';
  const legacyGate = (element) => (IS_LEGACY_DOMAIN ? <FeatureMoved /> : element);
  return (
    <LoginRequiredModalProvider>
      <LoadingProvider>
        <div className='flex flex-col min-h-screen'>
          <MigrationModal />
          {/* 옛 도메인에서는 이전 안내 모달이 뜨므로 겹치지 않게 뺀다 */}
          {!IS_LEGACY_DOMAIN && <NewsletterPrompt />}
          {/* 모바일 공고 관리 목록은 자체 헤더(뒤로 + Job postings)를 쓴다 */}
          {location.pathname === '/jobs/admin' && !isMobileViewport && (
            <MinimalGnb type='basic' hideActions />
          )}
          {/* 모바일은 위저드가 자체 헤더(뒤로 + 임시저장)를 갖고 있어 MinimalGnb를 띄우지 않는다 */}
          {isJobPostPage && !showMobileComingSoon && (
            <div className='hidden tabletH:block'>
              <MinimalGnb
                type='white'
                rightButtonLabel='Publish Job'
                onLeftButtonClick={handleSaveDraft}
                onRightButtonClick={handlePublish}
              />
            </div>
          )}
          {/* 공고 관리 상세: 데스크탑만 로고 GNB, 모바일은 자체 상단 바만 쓴다 (스페이서까지 숨기도록 래퍼로 감싼다) */}
          {isAdminJobDetailPath(location.pathname) && (
            <div className='hidden tabletH:block'>
              <LogoOnlyGNB type='plain' />
            </div>
          )}
          {showLogoOnlyGnb && (
            <LogoOnlyGNB
              className={isCheckDetailsPage ? 'hidden tabletH:flex' : ''}
              type={
                isMyPageDeleteAccountPage ||
                isRefundRequestPage ||
                isRefundSuccessPage ||
                isPathfinderPage ||
                isPathfinderOnboardingPage
                  ? 'white'
                  : 'gray'
              }
            />
          )}
          {!hideGnb &&
            (isBookingPage || isConfirmedPage ? (
              <div className='hidden tabletH:block'>
                <GNB />
              </div>
            ) : (
              <GNB />
            ))}

          <div className='flex flex-col flex-1'>
            <Routes>
              <Route path='/' element={<Home />} />
              <Route path='/error' element={<ErrorCS />} />
              <Route path='/session-expired' element={<SessionExpired />} />
              <Route path='/login' element={<Login />} />
              <Route path='/register' element={<Register />} />
              <Route path='/auth/google/callback' element={<GoogleCallback />} />
              <Route path='/auth/apple/callback' element={<AppleCallback />} />
              <Route path='/verify' element={<VerifyEmail />} />
              <Route path='/register/additional-info' element={<AdditionalInfo />} />
              <Route path='/password' element={<ForgotPassword />} />
              <Route path='/password/reset/verify' element={<ForgotPasswordVerify />} />
              <Route path='/password/reset' element={<ForgotPasswordNew />} />
              <Route path='/password/reset/success' element={<ForgotPasswordNewSuccess />} />

              <Route path='/community/qna' element={<Qna />} />
              <Route path='/community/qna/:qnaId' element={<QnaDetail />} />
              <Route path='/community/qna/search' element={<QnaSearch />} />
              <Route path='/faq' element={<Faq />} />

              <Route path='/article' element={<Article />} />
              {/* 어드민 화면은 /article에 통합됐다 (역할에 따라 그리드가 바뀜) */}
              <Route path='/article/admin' element={<Navigate to='/article' replace />} />
              <Route path='/article/:slug' element={<ArticleDetail />} />

              {/* 경로 뒤 :version은 시행일(2026-06-11). 없으면 최신 버전을 보여준다 */}
              <Route path='/terms' element={<TermsPage />} />
              <Route path='/terms/:version' element={<TermsPage />} />
              <Route path='/privacy' element={<PrivacyPage />} />
              <Route path='/privacy/:version' element={<PrivacyPage />} />
              <Route path='/business/terms' element={<BusinessTermsPage />} />
              <Route path='/business/terms/:version' element={<BusinessTermsPage />} />
              <Route path='/business/privacy' element={<BusinessPrivacyPage />} />
              <Route path='/business/privacy/:version' element={<BusinessPrivacyPage />} />
              <Route path='/refund-policy' element={<RefundPolicyPage />} />
              <Route path='/refund-policy/:version' element={<RefundPolicyPage />} />

              <Route
                path='/community/kpathport/payments'
                element={
                  <PaymentRoute>
                    <Payments />
                  </PaymentRoute>
                }
              />
              <Route
                path='/community/kpathport/bank-transfer'
                element={
                  <PaymentRoute>
                    <BankTransfer />
                  </PaymentRoute>
                }
              />
              <Route
                path='/community/kpathport/payments/success'
                element={
                  <PaymentRoute>
                    <PaymentSuccess />
                  </PaymentRoute>
                }
              />
              <Route
                path='/community/kpathport/payments/fail'
                element={
                  <PaymentRoute>
                    <PaymentFail />
                  </PaymentRoute>
                }
              />
              <Route path='/community/kpathport/discover' element={<Discover />} />
              <Route
                path='/community/kpathport/myrewards'
                element={<Navigate to='/community/kpathport/pathfeed' replace />}
              />
              <Route path='/community/kpathport/pathfeed' element={<PathFeed />} />
              <Route
                path='/community/kpathport/culture'
                element={<PathFeed category='culture' />}
              />
              <Route path='/community/kpathport/career' element={<PathFeed category='career' />} />
              <Route path='/community/kpathport/etc' element={<PathFeed category='etc' />} />
              <Route
                path='/community/kpathport/step02'
                element={<Navigate to='/community/kpathport/career' replace />}
              />
              <Route
                path='/community/kpathport/step0:legacyStep'
                element={<Navigate to='/community/kpathport/culture' replace />}
              />
              <Route path='/community/kpathport/workshop/:workshopId' element={<Workshop />} />
              <Route
                path='/community/kpathport/booking'
                element={
                  <PersonalOnlyRoute>
                    <Booking />
                  </PersonalOnlyRoute>
                }
              />
              <Route path='/community/kpathport/check-details' element={<CheckDetails />} />
              <Route path='/community/kpathport/confirmed' element={<Confirmed />} />

              <Route
                path='/career'
                element={legacyGate(
                  <PersonalOnlyRoute>
                    <Career />
                  </PersonalOnlyRoute>,
                )}
              />
              <Route path='/career/resumes/guide' element={legacyGate(<CareerResumeGuide />)} />
              <Route
                path='/career/resumes/new'
                element={legacyGate(
                  <PersonalOnlyRoute>
                    <CreateResume />
                  </PersonalOnlyRoute>,
                )}
              />
              <Route
                path='/career/resumes/:resumeId'
                element={legacyGate(
                  <PersonalOnlyRoute>
                    <CreateResume />
                  </PersonalOnlyRoute>,
                )}
              />
              <Route
                path='/career/resumes/:resumeId/success'
                element={legacyGate(
                  <PersonalOnlyRoute>
                    <CreateResumeSuccess />
                  </PersonalOnlyRoute>,
                )}
              />

              <Route path='/jobs' element={legacyGate(<JobBoard />)} />
              <Route
                path='/jobs/admin'
                element={
                  <JobManagerRoute>
                    <JobBoard key='management' management />
                  </JobManagerRoute>
                }
              />
              <Route path='/jobs/:jobId' element={legacyGate(<JobBoardDetail />)} />

              <Route
                path='/jobs/admin/:jobId'
                element={
                  <JobManagerRoute>
                    <JobBoardAdminDetail />
                  </JobManagerRoute>
                }
              />
              <Route
                path='/jobs/admin/new'
                element={
                  <JobManagerRoute>
                    <JobPost ref={formRef} />
                  </JobManagerRoute>
                }
              />
              <Route
                path='/jobs/admin/:jobId/edit'
                element={
                  <JobManagerRoute>
                    <JobPost ref={formRef} />
                  </JobManagerRoute>
                }
              />
              <Route path='/jobs/:jobId/unavailable' element={<DetailUnavailable />} />
              <Route path='/jobs/hidden/all' element={<JobHidden />} />

              <Route path='/mypage' element={<MyPage />} />
              <Route path='/mypage/delete-account' element={<DeleteAccount />} />
              <Route
                path='/mypage/resumes/all'
                element={
                  <PersonalOnlyRoute>
                    <MyResumes />
                  </PersonalOnlyRoute>
                }
              />
              <Route path='/mypage/:invoiceNumber/refund' element={<RequestRefund />} />
              <Route path='/mypage/:invoiceNumber/refund/success' element={<RefundSuccess />} />
              <Route
                path='/admin/workshop/new'
                element={
                  <AdminOnlyRoute>
                    <MobileUnsupportedRoute>
                      <WorkshopCreate />
                    </MobileUnsupportedRoute>
                  </AdminOnlyRoute>
                }
              />
              <Route
                path='/admin/workshop/:workshopId/edit'
                element={
                  <AdminOnlyRoute>
                    <MobileUnsupportedRoute>
                      <WorkshopCreate />
                    </MobileUnsupportedRoute>
                  </AdminOnlyRoute>
                }
              />
              <Route
                path='/admin/workshop/:workshopId'
                element={
                  <AdminOnlyRoute>
                    <MobileUnsupportedRoute>
                      <WorkshopManagementDetail />
                    </MobileUnsupportedRoute>
                  </AdminOnlyRoute>
                }
              />

              <Route
                path='/pathfinder/onboarding'
                element={
                  IS_LEGACY_DOMAIN ? (
                    <LegacyRedirect />
                  ) : (
                    <PersonalOnlyRoute>
                      <Pathfinder />
                    </PersonalOnlyRoute>
                  )
                }
              />
              <Route
                path='/pathfinder'
                element={
                  IS_LEGACY_DOMAIN ? (
                    <LegacyRedirect />
                  ) : (
                    <PersonalOnlyRoute>
                      <PathfinderEntry />
                    </PersonalOnlyRoute>
                  )
                }
              />
              <Route
                path='/pathfinder/dashboard'
                element={
                  IS_LEGACY_DOMAIN ? (
                    <LegacyRedirect />
                  ) : (
                    <PersonalOnlyRoute>
                      <PathfinderDashboard />
                    </PersonalOnlyRoute>
                  )
                }
              />

              {/* 레거시 /articles 링크는 실제 Article 페이지로 보낸다 */}
              <Route path='/articles' element={<Navigate to='/article' replace />} />
              {/* 2days project — Koready Skills 로 옮겼다. 옛 주소(메일 · 공유 링크)는 같은 경로로 넘긴다 */}
              <Route path='/2days/*' element={<TwoDaysMoved />} />
              <Route path='/2days' element={<TwoDaysMoved />} />
              <Route path='*' element={<RedirectToError />} />

              {/* 오잉작명소 */}
              <Route path='/korean-name-generator' element={<KnameStart />} />
              <Route path='/korean-name-generator/form' element={<KnameForm />} />
              <Route path='/korean-name-generator/form/mood' element={<KnameMood />} />
              <Route path='/korean-name-generator/result' element={<KnameResult />} />

              {/* 아이스브레이커 가챠 (워크샵용 임시 페이지) */}
              <Route path='/icebreaker-gacha' element={<IcebreakerGacha />} />
            </Routes>
          </div>
          {!hideFooter &&
            (IS_LEGACY_DOMAIN ? (
              <LegacyFooter />
            ) : (
              <FooterV2 beACreatorSection={showCreatorSection} />
            ))}
        </div>
      </LoadingProvider>
    </LoginRequiredModalProvider>
  );
}

export default AppInner;
